import React, { useEffect, useState } from "react";
import { api, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Accordion } from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Mail, Save, Plus, Trash2, Send, KeyRound, ShieldCheck, Download, ShoppingCart, Receipt, Store, Truck } from "lucide-react";
import { toast } from "sonner";
import { EigenEmailSjabloon } from "@/components/EigenEmailSjabloon";

const MERGE_VELDEN_INKOOP = [
  "leverancier_naam", "leverancier_contact", "klant_naam", "klant_contact",
  "klant_adres", "klant_email", "klant_telefoon", "aanleverdatum",
  "ordernummer", "referentie", "pakbon_nummer", "inkooporder_nummer", "timeslot", "afzender_naam",
];
const MERGE_VELDEN_FACTUUR = ["factuurnummer", "factuurdatum", "vervaldatum", "factuurtermijn", "openstaand_bedrag", "factuurbedrag", "rekeningnummer", "bedrijfsnaam", "klant_naam", "klant_contact", "afzender_naam"];
const MERGE_VELDEN_CREDIT = ["factuurnummer", "klant_naam", "klant_contact", "credit_reden", "bron_factuurnummer", "factuurbedrag", "afzender_naam"];
const MERGE_VELDEN_NORM_INKOOP = ["inkooporder_nummer", "leverancier_naam", "leverancier_contact", "aantal_regels", "aantal_stuks", "leveradres", "bedrijfsnaam", "afzender_naam"];
const MERGE_VELDEN_ACTIVATIE = ["bedrijfsnaam", "contact_naam", "voornaam", "klantcode", "login_link", "afzender_naam"];
const MERGE_VELDEN_VERZEND = ["klant_naam", "klant_contact", "tracktrace_code", "tracktrace_url", "vervoerder", "vervoerder_tekst", "ordernummer", "ordernummer_tekst", "pakbon_nummer", "verzenddatum", "afzender_naam"];

const TAAL_LABELS = { nl: "Nederlands", en: "Engels", de: "Duits" };

function MergeChips({ velden }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {velden.map((v) => <code key={v} className="text-[11px] bg-secondary px-1.5 py-0.5 rounded text-muted-foreground">{`{{${v}}}`}</code>)}
    </div>
  );
}

function TaalKiezer({ taal, setTaal, prefix }) {
  return (
    <div className="flex gap-1.5">
      {["nl", "en", "de"].map((t) => (
        <button key={t} type="button" onClick={() => setTaal(t)} data-testid={`${prefix}-${t}`}
          className={`px-3 py-1.5 text-sm rounded-md border ${taal === t ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:text-foreground"}`}>
          {TAAL_LABELS[t]}
        </button>
      ))}
    </div>
  );
}

// Inklapbaar kaart-omhulsel voor een vast systeemsjabloon (zonder Accordion — eigen open-state).
function SysteemKaart({ titel, subtitel, testid, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-lg bg-background overflow-hidden" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/40 transition-colors">
        <div className="p-1.5 rounded-md bg-secondary shrink-0"><Mail className="h-4 w-4 text-muted-foreground" /></div>
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">{titel}</div>
          {subtitel && <div className="text-xs text-muted-foreground truncate">{subtitel}</div>}
        </div>
        <Badge variant="outline" className="shrink-0 text-[10px]">Systeem</Badge>
        <span className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">{children}</div>}
    </div>
  );
}

function AfzenderDialog({ afzender, onSaved }) {
  const isEdit = !!afzender;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ naam: "", email: "", api_key: "", bcc: "", forward_naar: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF({
      naam: afzender?.naam || "", email: afzender?.email || "", api_key: "",
      bcc: afzender?.bcc || "", forward_naar: afzender?.forward_naar || "",
    });
  }, [open, afzender]);

  const submit = async () => {
    if (!f.email.trim()) { toast.error("E-mailadres is verplicht"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/email/afzenders/${afzender.id}`, f);
      else await api.post("/email/afzenders", f);
      toast.success(isEdit ? "Afzender bijgewerkt" : "Afzender toegevoegd");
      setOpen(false); onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit
          ? <Button variant="ghost" size="sm" data-testid={`afzender-edit-${afzender.id}`}>Bewerken</Button>
          : <Button variant="outline" size="sm" data-testid="afzender-add-btn"><Plus className="h-4 w-4 mr-1" />Afzender</Button>}
      </DialogTrigger>
      <DialogContent data-testid="afzender-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Afzender bewerken" : "Nieuwe afzender"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs text-muted-foreground">Weergavenaam</Label>
            <Input value={f.naam} onChange={(e) => setF({ ...f, naam: e.target.value })}
              placeholder="Bijv. Inkoop NextProduct" data-testid="afzender-naam" /></div>
          <div><Label className="text-xs text-muted-foreground">E-mailadres (geverifieerd domein in Resend)</Label>
            <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
              placeholder="inkoop@jouwdomein.nl" data-testid="afzender-email" /></div>
          <div><Label className="text-xs text-muted-foreground flex items-center gap-1"><KeyRound className="h-3 w-3" /> Resend API-key {isEdit && "(leeg laten = ongewijzigd)"}</Label>
            <Input type="password" value={f.api_key} onChange={(e) => setF({ ...f, api_key: e.target.value })}
              placeholder={isEdit && afzender?.heeft_api_key ? "•••••••• (opgeslagen)" : "re_…"} data-testid="afzender-apikey" />
            <p className="text-[11px] text-muted-foreground mt-1">Eigen key per account/domein. Leeg = standaard-key uit de omgeving.</p></div>
          <div><Label className="text-xs text-muted-foreground">Vaste BCC (optioneel)</Label>
            <Input value={f.bcc} onChange={(e) => setF({ ...f, bcc: e.target.value })}
              placeholder="kopie@jouwdomein.nl" data-testid="afzender-bcc" /></div>
          <div>
            <Label className="text-xs text-muted-foreground">Inkomende mail doorsturen naar (optioneel)</Label>
            <Input value={f.forward_naar} onChange={(e) => setF({ ...f, forward_naar: e.target.value })}
              placeholder="max@nextproduct.nl" data-testid="afzender-forward" />
            <p className="text-[11px] text-muted-foreground mt-1">
              Elke mail die via de Resend inbound-webhook op dit adres binnenkomt wordt automatisch als "Fwd:" doorgestuurd naar dit adres.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} disabled={saving} data-testid="afzender-save">{saving ? "Opslaan…" : "Opslaan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TABS = [
  { key: "inkoop", label: "Inkoop", icon: ShoppingCart },
  { key: "verkoop", label: "Verkoop", icon: Store },
  { key: "facturatie", label: "Facturatie", icon: Receipt },
];

export default function InstellingenEmail() {
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [afzenders, setAfzenders] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [gebruikers, setGebruikers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testNaar, setTestNaar] = useState("");
  const [factTaal, setFactTaal] = useState("nl");
  const [creditTaal, setCreditTaal] = useState("nl");
  const [activatieTaal, setActivatieTaal] = useState("nl");
  const [verzendTaal, setVerzendTaal] = useState("nl");
  const [tab, setTab] = useState("inkoop");
  const [addBusy, setAddBusy] = useState(false);

  const loadTemplates = async () => {
    try { const { data } = await api.get("/email-templates"); setTemplates(data || []); } catch { setTemplates([]); }
  };
  const load = async () => {
    const [{ data: a }, { data: c }] = await Promise.all([
      api.get("/email/afzenders"), api.get("/instellingen/email"),
    ]);
    setAfzenders(a || []); setCfg(c);
    loadTemplates();
    try { const { data: g } = await api.get("/gebruikers"); setGebruikers(g || []); } catch { setGebruikers([]); }
  };
  useEffect(() => { load(); }, []);

  const setCfgField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setFactuurTekst = (taal, key, v) => setCfg((c) => ({
    ...c, factuur_teksten: { ...(c.factuur_teksten || {}), [taal]: { ...((c.factuur_teksten || {})[taal] || {}), [key]: v } },
  }));
  const setBetaaldTekst = (taal, key, v) => setCfg((c) => ({
    ...c, factuur_betaald_teksten: { ...(c.factuur_betaald_teksten || {}), [taal]: { ...((c.factuur_betaald_teksten || {})[taal] || {}), [key]: v } },
  }));
  const setCreditTekst = (taal, key, v) => setCfg((c) => ({
    ...c, credit_teksten: { ...(c.credit_teksten || {}), [taal]: { ...((c.credit_teksten || {})[taal] || {}), [key]: v } },
  }));
  const setActivatieTekst = (taal, key, v) => setCfg((c) => ({
    ...c, activatie_teksten: { ...(c.activatie_teksten || {}), [taal]: { ...((c.activatie_teksten || {})[taal] || {}), [key]: v } },
  }));
  const setVerzendTekst = (taal, key, v) => setCfg((c) => ({
    ...c, verzendbevestiging_teksten: { ...(c.verzendbevestiging_teksten || {}), [taal]: { ...((c.verzendbevestiging_teksten || {})[taal] || {}), [key]: v } },
  }));

  const downloadVelden = () => downloadFile("/email/template-velden/export", "email-velden.csv");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/instellingen/email", cfg);
      setCfg(data); toast.success("E-mailinstellingen opgeslagen");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Alleen admins kunnen dit wijzigen" : "Opslaan mislukt");
    } finally { setSaving(false); }
  };

  const addTemplate = async (categorie) => {
    setAddBusy(true);
    try {
      await api.post("/email-templates", { categorie, naam: "Nieuw e-mailsjabloon", handtekening_type: "verzender" });
      toast.success("Sjabloon toegevoegd");
      await loadTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toevoegen mislukt"); }
    finally { setAddBusy(false); }
  };

  const delAfzender = async (id) => {
    if (!window.confirm("Afzender verwijderen?")) return;
    await api.delete(`/email/afzenders/${id}`);
    toast.success("Verwijderd"); load();
  };
  const testMail = async (afzender_id) => {
    if (!testNaar.trim()) { toast.error("Vul een test-ontvanger in"); return; }
    try { await api.post("/email/test", { afzender_id, naar: testNaar.trim() }); toast.success("Testmail verstuurd"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
  };
  const testVerzendbevestiging = async () => {
    if (!testNaar.trim()) { toast.error("Vul hierboven bij Afzenders een test-ontvanger in"); return; }
    try {
      await api.post("/verzendbevestigingen/test", { naar: testNaar.trim(), taal: verzendTaal });
      toast.success("Voorbeeld-verzendbevestiging verstuurd");
    } catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
  };

  if (!cfg) return <div className="text-muted-foreground">Laden…</div>;

  const eigenVoorCategorie = (c) => templates.filter((t) => t.categorie === c);

  const EigenSectie = ({ categorie }) => {
    const rijen = eigenVoorCategorie(categorie);
    return (
      <div className="space-y-3" data-testid={`eigen-sectie-${categorie}`}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-muted-foreground">Eigen e-mailsjablonen</h3>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => addTemplate(categorie)} disabled={addBusy} data-testid={`sjabloon-toevoegen-${categorie}`}>
              <Plus className="h-4 w-4 mr-1" />Nieuwe e-mail
            </Button>
          )}
        </div>
        {rijen.length === 0
          ? <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-4 text-center">Nog geen eigen sjablonen in deze categorie. Voeg er één toe met "Nieuwe e-mail".</div>
          : (
            <Accordion type="multiple" className="space-y-2">
              {rijen.map((t) => (
                <EigenEmailSjabloon key={t.id} tpl={t} afzenders={afzenders} gebruikers={gebruikers} isAdmin={isAdmin}
                  onSaved={loadTemplates} onDeleted={loadTemplates} />
              ))}
            </Accordion>
          )}
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="instellingen-email">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-lg bg-secondary flex items-center justify-center"><Mail className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">E-mail &amp; verzenden</h1>
          <p className="text-sm text-muted-foreground mt-1">Afzenders, verbinding en e-mailsjablonen per categorie (inkoop, verkoop, facturatie).</p>
        </div>
      </div>

      {/* Afzenders */}
      <div className="bg-card border border-border rounded-xl p-6" data-testid="afzenders-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-medium">Afzenders</h2>
            <p className="text-sm text-muted-foreground">Elk met een eigen Resend API-key (account/domein) en optionele vaste BCC.</p>
          </div>
          {isAdmin && <AfzenderDialog onSaved={load} />}
        </div>
        <div className="space-y-2">
          {afzenders.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3" data-testid={`afzender-${a.id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{a.naam || a.email}</div>
                <div className="text-xs text-muted-foreground truncate">{a.email}{a.bcc ? ` · BCC: ${a.bcc}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {a.heeft_api_key
                  ? <Badge variant="outline" className="text-emerald-600 border-emerald-300 gap-1"><ShieldCheck className="h-3 w-3" />Key</Badge>
                  : <Badge variant="outline" className="text-amber-600 border-amber-300">Standaard-key</Badge>}
                {isAdmin && <Button variant="ghost" size="sm" onClick={() => testMail(a.id)} data-testid={`afzender-test-${a.id}`}><Send className="h-4 w-4" /></Button>}
                {isAdmin && <AfzenderDialog afzender={a} onSaved={load} />}
                {isAdmin && <button onClick={() => delAfzender(a.id)} className="text-muted-foreground hover:text-destructive" data-testid={`afzender-del-${a.id}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
          ))}
          {afzenders.length === 0 && <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-4 text-center">Nog geen afzenders. Voeg er één toe.</div>}
        </div>
        {isAdmin && afzenders.length > 0 && (
          <div className="flex items-end gap-2 mt-4 pt-4 border-t border-border">
            <div className="flex-1"><Label className="text-xs text-muted-foreground">Test-ontvanger (klik <Send className="h-3 w-3 inline" /> bij een afzender)</Label>
              <Input value={testNaar} onChange={(e) => setTestNaar(e.target.value)} placeholder="jij@voorbeeld.nl" data-testid="test-naar" /></div>
          </div>
        )}
      </div>

      {/* Verbinding & toewijzing */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="verbinding-section">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-medium flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> Verbinding &amp; toewijzing</h2>
          {cfg.heeft_resend_key
            ? <Badge variant="outline" className="text-emerald-600 border-emerald-300 gap-1"><ShieldCheck className="h-3 w-3" />Key ingesteld</Badge>
            : <Badge variant="outline" className="text-amber-600 border-amber-300">Geen key</Badge>}
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Standaard Resend API-key {cfg.heeft_resend_key && cfg.resend_key_hint ? `(huidig: ${cfg.resend_key_hint})` : ""}</Label>
          <Input type="password" value={cfg.resend_api_key || ""} disabled={!isAdmin}
            onChange={(e) => setCfgField("resend_api_key", e.target.value)}
            placeholder={cfg.heeft_resend_key ? "•••••••• (laat leeg om ongewijzigd te laten)" : "re_…"} data-testid="resend-global-key" />
          <p className="text-[11px] text-muted-foreground mt-1">Wordt gebruikt voor afzenders zonder eigen key. Nooit volledig getoond.</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label className="text-xs text-muted-foreground">Inkoop-afzender</Label>
            <select value={cfg.inkoop_afzender_id || ""} disabled={!isAdmin} onChange={(e) => setCfgField("inkoop_afzender_id", e.target.value)}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm" data-testid="inkoop-afzender-select">
              <option value="">— Geen —</option>{afzenders.map((a) => <option key={a.id} value={a.id}>{a.naam || a.email}</option>)}
            </select></div>
          <div><Label className="text-xs text-muted-foreground">Facturatie-afzender</Label>
            <select value={cfg.facturatie_afzender_id || ""} disabled={!isAdmin} onChange={(e) => setCfgField("facturatie_afzender_id", e.target.value)}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm" data-testid="facturatie-afzender-select">
              <option value="">— Geen —</option>{afzenders.map((a) => <option key={a.id} value={a.id}>{a.naam || a.email}</option>)}
            </select></div>
          <div><Label className="text-xs text-muted-foreground">Algemene afzender</Label>
            <select value={cfg.algemeen_afzender_id || ""} disabled={!isAdmin} onChange={(e) => setCfgField("algemeen_afzender_id", e.target.value)}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm" data-testid="algemeen-afzender-select">
              <option value="">— Geen —</option>{afzenders.map((a) => <option key={a.id} value={a.id}>{a.naam || a.email}</option>)}
            </select>
            <div className="text-[11px] text-muted-foreground mt-1">Voor webshop-uitnodigingen, activatie-mails, wachtwoord-herstel en campagnes.</div>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <div className="font-medium text-sm">"Doorzetten naar leverancier" inschakelen</div>
            <div className="text-xs text-muted-foreground">Toont de actie op verkoopkansen om een order direct naar een leverancier te mailen.</div>
          </div>
          <Switch checked={!!cfg.doorzetten_actief} disabled={!isAdmin} onCheckedChange={(v) => setCfgField("doorzetten_actief", v)} data-testid="doorzetten-toggle" />
        </div>
      </div>

      {/* E-mailsjablonen met subtabs */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-5" data-testid="templates-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-display text-xl font-medium">E-mailsjablonen</h2>
          <Button variant="outline" size="sm" onClick={downloadVelden} data-testid="download-velden-btn"><Download className="h-4 w-4 mr-1" /> Veldenlijst</Button>
        </div>

        <div className="flex gap-1.5" data-testid="email-subtabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`email-tab-${t.key}`}
                className={`px-4 py-2 rounded-full text-sm border transition-colors flex items-center gap-1.5 ${tab === t.key ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:bg-secondary"}`}>
                <Icon className="h-4 w-4" />{t.label}
              </button>
            );
          })}
        </div>

        {/* INKOOP */}
        {tab === "inkoop" && (
          <div className="space-y-3" data-testid="tab-inhoud-inkoop">
            <SysteemKaart titel="Doorzetten naar leverancier" subtitel="Order direct vanuit een verkoopkans naar een leverancier mailen" testid="sys-inkoop-doorzet">
              <div className="text-xs text-muted-foreground bg-secondary/50 rounded-md p-3" data-testid="inkoop-subject-info">
                Onderwerp is automatisch: <code className="bg-secondary px-1 rounded">[2 letters leverancier]NP [maand]-[volgnr] - [klantnaam]</code> — bijv. <strong>HONP 6-1 - Holland &amp; Barrett</strong>.
              </div>
              <div><Label className="text-xs text-muted-foreground">Bericht</Label>
                <Textarea rows={9} value={cfg.template_body || ""} disabled={!isAdmin} onChange={(e) => setCfgField("template_body", e.target.value)} data-testid="template-body" /></div>
              <MergeChips velden={MERGE_VELDEN_INKOOP} />
            </SysteemKaart>
            <SysteemKaart titel="Normale inkooporder" subtitel="Bij 'Verstuur per mail' op een inkooporder (levering op eigen adres)" testid="sys-inkoop-normaal">
              <div><Label className="text-xs text-muted-foreground">Onderwerp</Label>
                <Input value={cfg.inkoop_subject || ""} disabled={!isAdmin} onChange={(e) => setCfgField("inkoop_subject", e.target.value)} data-testid="inkoop-norm-subject" /></div>
              <div><Label className="text-xs text-muted-foreground">Bericht</Label>
                <Textarea rows={9} value={cfg.inkoop_body || ""} disabled={!isAdmin} onChange={(e) => setCfgField("inkoop_body", e.target.value)} data-testid="inkoop-norm-body" /></div>
              <MergeChips velden={MERGE_VELDEN_NORM_INKOOP} />
            </SysteemKaart>
            <EigenSectie categorie="inkoop" />
          </div>
        )}

        {/* VERKOOP */}
        {tab === "verkoop" && (
          <div className="space-y-3" data-testid="tab-inhoud-verkoop">
            <SysteemKaart titel="Webshop — account geactiveerd" subtitel="Automatische mail naar de aanmelder zodra je zijn/haar account goedkeurt" testid="sys-activatie" defaultOpen>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <div className="font-medium text-sm">Activatie-mail automatisch versturen</div>
                  <div className="text-xs text-muted-foreground">Bij het goedkeuren van een webshop-aanvraag krijgt de aanmelder deze mail. Taal volgt het klantland.</div>
                </div>
                <Switch checked={!!cfg.activatie_mail_actief} disabled={!isAdmin} onCheckedChange={(v) => setCfgField("activatie_mail_actief", v)} data-testid="activatie-toggle" />
              </div>
              <TaalKiezer taal={activatieTaal} setTaal={setActivatieTaal} prefix="activatie-taal-tab" />
              <div><Label className="text-xs text-muted-foreground">Onderwerp ({TAAL_LABELS[activatieTaal]})</Label>
                <Input value={(cfg.activatie_teksten?.[activatieTaal]?.subject) || ""} disabled={!isAdmin}
                  onChange={(e) => setActivatieTekst(activatieTaal, "subject", e.target.value)} data-testid="activatie-subject" /></div>
              <div><Label className="text-xs text-muted-foreground">Bericht ({TAAL_LABELS[activatieTaal]})</Label>
                <Textarea rows={8} value={(cfg.activatie_teksten?.[activatieTaal]?.body) || ""} disabled={!isAdmin}
                  onChange={(e) => setActivatieTekst(activatieTaal, "body", e.target.value)} data-testid="activatie-body" /></div>
              <MergeChips velden={MERGE_VELDEN_ACTIVATIE} />
            </SysteemKaart>
            <SysteemKaart titel="Verzendbevestiging — track & trace" subtitel="Automatische mail naar de klant zodra MyParcel een track & trace teruggeeft (elk uur gecontroleerd)" testid="sys-verzendbevestiging" defaultOpen>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <div className="font-medium text-sm">Verzendbevestiging automatisch versturen</div>
                  <div className="text-xs text-muted-foreground">Elk uur worden de track &amp; traces bij MyParcel opgehaald; zodra er één beschikbaar is krijgt de klant deze mail. Taal volgt het klantland.</div>
                </div>
                <Switch checked={!!cfg.verzendbevestiging_mail_actief} disabled={!isAdmin} onCheckedChange={(v) => setCfgField("verzendbevestiging_mail_actief", v)} data-testid="verzendbevestiging-toggle" />
              </div>
              <TaalKiezer taal={verzendTaal} setTaal={setVerzendTaal} prefix="verzend-taal-tab" />
              <div><Label className="text-xs text-muted-foreground">Onderwerp ({TAAL_LABELS[verzendTaal]})</Label>
                <Input value={(cfg.verzendbevestiging_teksten?.[verzendTaal]?.subject) || ""} disabled={!isAdmin}
                  onChange={(e) => setVerzendTekst(verzendTaal, "subject", e.target.value)} data-testid="verzendbevestiging-subject" /></div>
              <div><Label className="text-xs text-muted-foreground">Bericht ({TAAL_LABELS[verzendTaal]})</Label>
                <Textarea rows={9} value={(cfg.verzendbevestiging_teksten?.[verzendTaal]?.body) || ""} disabled={!isAdmin}
                  onChange={(e) => setVerzendTekst(verzendTaal, "body", e.target.value)} data-testid="verzendbevestiging-body" /></div>
              <MergeChips velden={MERGE_VELDEN_VERZEND} />
              {isAdmin && (
                <div className="flex items-center justify-between gap-2 pt-1">
                  <p className="text-[11px] text-muted-foreground">Sla eerst op; stuur daarna een voorbeeld naar de test-ontvanger (zie Afzenders).</p>
                  <Button variant="outline" size="sm" onClick={testVerzendbevestiging} data-testid="verzendbevestiging-test-btn">
                    <Truck className="h-4 w-4 mr-1" /> Voorbeeld versturen
                  </Button>
                </div>
              )}
            </SysteemKaart>
            <EigenSectie categorie="verkoop" />
          </div>
        )}

        {/* FACTURATIE */}
        {tab === "facturatie" && (
          <div className="space-y-3" data-testid="tab-inhoud-facturatie">
            <SysteemKaart titel="Factuur versturen" subtitel="Per taal een eigen onderwerp en bericht (taal volgt klantland)" testid="sys-factuur">
              <TaalKiezer taal={factTaal} setTaal={setFactTaal} prefix="factuur-taal-tab" />
              <div><Label className="text-xs text-muted-foreground">Onderwerp ({TAAL_LABELS[factTaal]})</Label>
                <Input value={(cfg.factuur_teksten?.[factTaal]?.subject) || ""} disabled={!isAdmin} onChange={(e) => setFactuurTekst(factTaal, "subject", e.target.value)} data-testid="factuur-subject" /></div>
              <div><Label className="text-xs text-muted-foreground">Bericht ({TAAL_LABELS[factTaal]})</Label>
                <Textarea rows={6} value={(cfg.factuur_teksten?.[factTaal]?.body) || ""} disabled={!isAdmin} onChange={(e) => setFactuurTekst(factTaal, "body", e.target.value)} data-testid="factuur-body" /></div>
              <MergeChips velden={MERGE_VELDEN_FACTUUR} />
            </SysteemKaart>
            <SysteemKaart titel="Factuur die al betaald is (webshop / iDEAL)" subtitel="Geen betaalinstructies maar een bevestiging" testid="sys-factuur-betaald">
              <TaalKiezer taal={factTaal} setTaal={setFactTaal} prefix="betaald-taal-tab" />
              <div><Label className="text-xs text-muted-foreground">Onderwerp ({TAAL_LABELS[factTaal]})</Label>
                <Input value={(cfg.factuur_betaald_teksten?.[factTaal]?.subject) || ""} disabled={!isAdmin} onChange={(e) => setBetaaldTekst(factTaal, "subject", e.target.value)} data-testid="factuur-betaald-subject" /></div>
              <div><Label className="text-xs text-muted-foreground">Bericht ({TAAL_LABELS[factTaal]})</Label>
                <Textarea rows={6} value={(cfg.factuur_betaald_teksten?.[factTaal]?.body) || ""} disabled={!isAdmin} onChange={(e) => setBetaaldTekst(factTaal, "body", e.target.value)} data-testid="factuur-betaald-body" /></div>
              <MergeChips velden={[...MERGE_VELDEN_FACTUUR, "betaalmethode", "betaald_op", "betaald_bedrag", "ordernummer", "ordernummer_tekst"]} />
            </SysteemKaart>
            <SysteemKaart titel="Creditfactuur — kopie naar boekhouding" subtitel="Automatische kopie (met PDF) bij het aanmaken van een creditfactuur" testid="sys-credit">
              <div><Label className="text-xs text-muted-foreground">Boekhouding e-mailadres</Label>
                <Input value={cfg.boekhouding_email || ""} disabled={!isAdmin} onChange={(e) => setCfgField("boekhouding_email", e.target.value)} placeholder="nextproduct@boekhouding.nl" data-testid="boekhouding-email" />
                <p className="text-[11px] text-muted-foreground mt-1">Laat leeg om de automatische kopie uit te schakelen.</p></div>
              <TaalKiezer taal={creditTaal} setTaal={setCreditTaal} prefix="credit-taal-tab" />
              <div><Label className="text-xs text-muted-foreground">Onderwerp ({TAAL_LABELS[creditTaal]})</Label>
                <Input value={(cfg.credit_teksten?.[creditTaal]?.subject) || ""} disabled={!isAdmin} onChange={(e) => setCreditTekst(creditTaal, "subject", e.target.value)} data-testid="credit-subject" /></div>
              <div><Label className="text-xs text-muted-foreground">Bericht ({TAAL_LABELS[creditTaal]})</Label>
                <Textarea rows={8} value={(cfg.credit_teksten?.[creditTaal]?.body) || ""} disabled={!isAdmin} onChange={(e) => setCreditTekst(creditTaal, "body", e.target.value)} data-testid="credit-body" /></div>
              <MergeChips velden={MERGE_VELDEN_CREDIT} />
            </SysteemKaart>
            <EigenSectie categorie="facturatie" />
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="flex justify-end sticky bottom-4">
          <Button onClick={save} disabled={saving} data-testid="email-instellingen-save" className="shadow-lg">
            <Save className="h-4 w-4 mr-2" />{saving ? "Opslaan…" : "Instellingen opslaan"}
          </Button>
        </div>
      )}
      {!isAdmin && <p className="text-xs text-muted-foreground">Alleen beheerders kunnen deze instellingen wijzigen. Eigen sjablonen worden per kaart opgeslagen.</p>}
    </div>
  );
}
